import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { getPascoService } from '@src/container';
import type { PascoDTO } from '@src/application/pascos/dto';

/**
 * Hook: useReviewPasco
 * Marks a PASCO record as reviewed
 *
 * @param options - React Query mutation options
 * @returns Mutation result for reviewing PASCO
 *
 * @example
 * ```typescript
 * const { mutate: reviewPasco, isPending, error } = useReviewPasco({
 *     onSuccess: (reviewedPasco) => {
 *         console.log('PASCO reviewed:', reviewedPasco);
 *     }
 * });
 *
 * // Usage
 * reviewPasco('pasco-123');
 * ```
 */
export function useReviewPasco(
    options?: Omit<UseMutationOptions<PascoDTO, Error, string>, 'mutationFn'>
) {
    const pascoService = getPascoService();
    const queryClient = useQueryClient();

    // Extract user's onSuccess before creating mutation
    const { onSuccess: userOnSuccess, ...restOptions } = options || {};

    return useMutation({
        ...restOptions,
        mutationFn: async (pascoId: string) => {
            return await pascoService.review(pascoId);
        },
        onSuccess: async (data, variables, context, extra) => {
            // Invalidate and refetch pascos list and pending review queries
            await queryClient.invalidateQueries({
                queryKey: ['pascos'],
                predicate: (query) =>
                    query.queryKey.length === 1 || // General pascos list
                    query.queryKey[1] === 'pending-review' // Pending review queries
            });

            // Update the specific pasco in cache
            queryClient.setQueryData(['pascos', variables], data);

            // Call user's onSuccess callback if provided
            if (userOnSuccess) {
                await userOnSuccess(data, variables, context, extra);
            }
        }
    });
}